import React from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';
import AnimatedSection from '../../components/common/AnimatedSection';
import { mapsConfig } from '../../config/maps';
import { TelegramIcon, DiscordIcon, TwitterIcon, LinkedInIcon } from '../../components/icons';

// 控制页面顶部的横幅区域（带背景图和标题）
const HeroSection = styled('div', {
  width: '100%',
  background: '#0F172A',
  color: 'white',
  padding: '180px 0',
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
});

// 控制横幅区域的世界地图背景
const WorldMapBackground = styled('div', {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundImage: 'url("/images/contact1.jpeg")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  opacity: 0.6,
  pointerEvents: 'none',
  zIndex: 0,
  
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: 'radial-gradient(circle, rgba(98, 78, 226, 0.3) 1px, transparent 1px)',
    backgroundSize: '20px 20px',
  }
});

// 控制地图上的位置标记点
const LocationMarker = styled('div', {
  position: 'absolute',
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  background: 'rgba(149, 96, 229, 0.8)',
  boxShadow: '0 0 10px rgba(149, 96, 229, 0.8)',
  zIndex: 1,
  
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: 'rgba(227, 176, 255, 0.9)',
  }
});

// 控制横幅区域的内容（标题、描述）
const HeroContent = styled('div', {
  position: 'relative',
  zIndex: 2,
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '0 20px',
  
  '& h1': {
    fontSize: '46px',
    fontWeight: '700',
    marginBottom: '24px',
  },
  
  '& p': {
    fontSize: '20px',
    maxWidth: '700px',
    margin: '0 auto',
    lineHeight: '1.6',
    opacity: 0.8,
  }
});

// 控制整个联系我们页面的主容器
const ContactPageContainer = styled('div', {
  padding: '80px 0 60px',
  maxWidth: '1200px',
  margin: '0 auto',
});

// 联系页面标题组件已删除 - 当前未使用

// 控制主内容区的左右布局
const ContactContent = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr 1.2fr',
  gap: '60px',
  padding: '0 20px',
  
  '@media (max-width: 920px)': {
    gridTemplateColumns: '1fr',
  }
});

// 控制左侧联系信息区域
const ContactInfo = styled('div', {
  '& .info-title': {
    fontSize: '28px',
    fontWeight: '600',
    marginBottom: '36px',
    color: '$headingColor',
  }
});

// 控制联系信息的详细内容区域
const ContactDetails = styled('div', {
  marginBottom: '50px',
});

// 控制每一项联系信息的样式
const ContactItem = styled('div', {
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: '30px',
  transition: 'transform 0.2s ease',
  
  '&:hover': {
    transform: 'translateX(5px)',
  },
  
  '& .contact-icon': {
    width: '52px',
    height: '52px',
    borderRadius: '12px',
    background: 'linear-gradient(145deg, #253555 0%, #1A2642 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '22px',
    fontSize: '24px',
    color: 'white',
    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)',
    border: '1px solid rgba(55, 65, 95, 0.7)',
  },
  
  '& .contact-content': {
    flex: 1,
  },
  
  '& .contact-label': {
    fontSize: '19px',
    fontWeight: '600',
    marginBottom: '10px',
    color: '$headingColor',
  },
  
  '& .contact-value': {
    fontSize: '16px',
    color: '$textSecondary',
    lineHeight: '1.6',
  },
  
  '& a': {
    color: '#6E8AFF',
    textDecoration: 'none',
    fontWeight: '500',
    transition: 'color 0.2s ease',
    
    '&:hover': {
      color: '#8DA2FF',
      textDecoration: 'underline',
    }
  }
});

// 控制社交媒体卡片列表的布局
const SocialLinks = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr',
  width: '100%',
  gap: '24px',
  marginTop: '24px',
  maxWidth: '100%',
  boxSizing: 'border-box',
  
  '@media (min-width: 768px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  
  '@media (min-width: 1200px)': {
    gridTemplateColumns: 'repeat(4, 1fr)',
  }
});

// 控制单个社交媒体卡片的样式
const SocialCard = styled('a', {
  display: 'flex',
  alignItems: 'center',
  padding: '22px 18px',
  background: 'linear-gradient(145deg, #1F2B47 0%, #0F172A 100%)',
  borderRadius: '14px',
  textDecoration: 'none',
  transition: 'all 0.3s ease',
  width: '100%',
  position: 'relative',
  border: '1px solid rgba(55, 65, 95, 0.8)',
  boxShadow: '0 6px 12px rgba(0, 0, 0, 0.25)',
  
  '&:hover': {
    background: 'linear-gradient(145deg, #253555 0%, #1A2642 100%)',
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.3)',
  },
  
  '& .social-icon': {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: '#3563E9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '22px',
    marginRight: '16px',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
  },
  
  '& .social-name': {
    fontSize: '16px',
    fontWeight: '600',
    color: 'white',
  },
  
  '& .illustration-space': {
    position: 'absolute',
    right: '20px',
    width: '60px',
    height: '60px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  }
});

// 控制右侧地图容器
const ContactMap = styled('div', {
  height: '420px',
  borderRadius: '18px',
  overflow: 'hidden',
  marginTop: '0',
  boxShadow: '0 15px 35px rgba(0, 0, 0, 0.25)',
  border: '1px solid rgba(55, 65, 95, 0.7)',
  position: 'relative',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.35)',
  },
  
  '& iframe': {
    width: '100%',
    height: '100%',
    border: 'none',
    borderRadius: '18px',
    filter: 'invert(90%) hue-rotate(180deg) brightness(0.85) contrast(0.9)',
  },
  
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
    borderRadius: '18px',
    boxShadow: 'inset 0 0 0 1px rgba(55, 65, 95, 0.5)'
  }
});

// 控制全球办公地点区域
const OfficesSection = styled('div', {
  padding: '80px 20px',
  maxWidth: '1200px',
  margin: '0 auto',
  
  '& .section-title': {
    fontSize: '32px',
    fontWeight: '700',
    marginBottom: '16px',
    textAlign: 'center',
    color: '$headingColor',
  },
  
  '& .section-subtitle': {
    fontSize: '18px',
    color: '$textSecondary',
    textAlign: 'center',
    maxWidth: '700px',
    margin: '0 auto 50px',
    lineHeight: '1.6',
  }
});

// 控制办公地点卡片列表的布局
const OfficesList = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
  gap: '40px',
  marginTop: '40px',
});

// 控制单个办公地点卡片的样式
const OfficeCard = styled('div', {
  background: 'linear-gradient(145deg, #1F2B47 0%, #0F172A 100%)',
  borderRadius: '18px',
  padding: '40px',
  boxShadow: '0 15px 30px rgba(0, 0, 0, 0.25)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  border: '1px solid rgba(55, 65, 95, 0.7)',
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '0',
    left: '0',
    width: '6px',
    height: '100%',
    background: 'linear-gradient(180deg, #3563E9 0%, #6E8AFF 100%)',
    borderTopLeftRadius: '18px',
    borderBottomLeftRadius: '18px',
  },
  
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 22px 45px rgba(0, 0, 0, 0.35)',
  },
  
  '& .office-city': {
    fontSize: '26px',
    fontWeight: '700',
    marginBottom: '22px',
    color: 'white',
  },
  
  '& .office-address': {
    fontSize: '16px',
    color: 'rgba(255, 255, 255, 0.7)',
    lineHeight: '1.6',
    marginBottom: '22px',
  },
  
  '& .office-phone': {
    fontSize: '16px',
    color: 'rgba(255, 255, 255, 0.7)',
    marginBottom: '10px',
    display: 'flex',
    alignItems: 'center',
    
    '&::before': {
      content: '"📞"',
      marginRight: '8px',
      fontSize: '18px',
    }
  },
  
  '& .office-email': {
    fontSize: '16px',
    color: 'rgba(255, 255, 255, 0.7)',
    display: 'flex',
    alignItems: 'center',
    
    '&::before': {
      content: '"✉️"',
      marginRight: '8px',
      fontSize: '18px',
    },
    
    '& a': {
      color: '#6E8AFF',
      textDecoration: 'none',
      
      '&:hover': {
        color: '#8DA2FF',
        textDecoration: 'underline',
      }
    }
  }
});

// 控制"关注我们"卡片的样式
const FollowUsCard = styled('div', {
  background: 'linear-gradient(145deg, #1F2B47 0%, #0F172A 100%)',
  borderRadius: '18px',
  padding: '36px',
  boxShadow: '0 12px 28px rgba(0, 0, 0, 0.25)',
  marginTop: '40px',
  width: '100%',
  border: '1px solid rgba(55, 65, 95, 0.7)',
  
  '& h3': {
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '24px',
    color: 'white',
    textAlign: 'center'
  }
});

// 联系我们页面组件
const ContactPage: React.FC = () => {
  const { t } = useTranslation();
  
  // 定义全球办公地点位置标记
  const locationMarkers = [
    { top: '30%', left: '20%' },    // 北美
    { top: '25%', left: '45%' },    // 欧洲
    { top: '32%', left: '52%' },    // 中东
    { top: '40%', left: '75%' },    // 亚洲1
    { top: '35%', left: '82%' },    // 亚洲2
    { top: '45%', left: '77%' },    // 亚洲3
    { top: '33%', left: '42%' },    // 南欧
    { top: '55%', left: '22%' },    // 南美
    { top: '65%', left: '60%' },    // 非洲
    { top: '75%', left: '85%' }     // 澳洲
  ];
  
  // 更新办公地点数据
  const offices = [
    {
      city: "香港",
      address: "葵涌興芳路223號新都会广场",
      phone: "+852 1234 5678",
      email: "hongkong@BITENGINE.com"
    },
    {
      city: t('contact.offices.office2.city'),
      address: t('contact.offices.office2.address'),
      phone: t('contact.offices.office2.phone'),
      email: t('contact.offices.office2.email')
    },
    {
      city: t('contact.offices.office3.city'),
      address: t('contact.offices.office3.address'),
      phone: t('contact.offices.office3.phone'),
      email: t('contact.offices.office3.email')
    }
  ];
  
  return (
    <>
      <HeroSection>
        <WorldMapBackground>
          {locationMarkers.map((marker, index) => (
            <LocationMarker key={index} style={{ top: marker.top, left: marker.left }} />
          ))}
        </WorldMapBackground>
        <HeroContent>
          <h1>{t('contact.title')}</h1>
          <p>我们试图更好地接近我们的客户，因此我们的员工分布在全球各地，可以全天候为您提供帮助</p>
        </HeroContent>
      </HeroSection>
      
      <ContactPageContainer>
        <ContactContent>
          <AnimatedSection direction="left" delay={0.2}>
            <ContactInfo>
              <h2 className="info-title">{t('contact.getInTouch')}</h2>
              <ContactDetails>
                <ContactItem>
                  <div className="contact-icon">📞</div>
                  <div className="contact-content">
                    <div className="contact-label">{t('contact.phone')}</div>
                    <div className="contact-value">+852 1234 5678</div>
                  </div>
                </ContactItem>
                <ContactItem>
                  <div className="contact-icon">✉️</div>
                  <div className="contact-content">
                    <div className="contact-label">{t('contact.email')}</div>
                    <div className="contact-value">
                      <a href="mailto:info@BITENGINE.com">info@BITENGINE.com</a>
                    </div>
                  </div>
                </ContactItem>
                <ContactItem>
                  <div className="contact-icon">🏢</div>
                  <div className="contact-content">
                    <div className="contact-label">{t('contact.headquarters')}</div>
                    <div className="contact-value">
                      興芳路223號新都会广场<br />
                      葵涌, 香港
                    </div>
                  </div>
                </ContactItem>
                <ContactItem>
                  <div className="contact-icon">⏰</div>
                  <div className="contact-content">
                    <div className="contact-label">{t('contact.workingHours')}</div>
                    <div className="contact-value">
                      {t('contact.mondayFriday')}: 9:00 AM - 6:00 PM<br />
                      {t('contact.saturdaySunday')}: {t('contact.closed')}
                    </div>
                  </div>
                </ContactItem>
              </ContactDetails>
            </ContactInfo>
          </AnimatedSection>
          <AnimatedSection direction="right" delay={0.2}>
            <div>
              <h2 className="info-title">{t('contact.mapPlaceholder')}</h2>
              <ContactMap>
                <iframe 
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${mapsConfig.defaultCenter.lng-0.005}%2C${mapsConfig.defaultCenter.lat-0.005}%2C${mapsConfig.defaultCenter.lng+0.005}%2C${mapsConfig.defaultCenter.lat+0.005}&amp;layer=mapnik&amp;marker=${mapsConfig.defaultCenter.lat}%2C${mapsConfig.defaultCenter.lng}`}
                  title={t('contact.mapPlaceholder')}
                  aria-label={t('contact.mapPlaceholder')}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </ContactMap>
            </div>
          </AnimatedSection>
        </ContactContent>
        <FollowUsCard style={{ marginTop: 40 }}>
          <h3>{t('contact.followUs')}</h3>
          <SocialLinks>
            <SocialCard href="#" target="_blank" aria-label="Twitter">
              <div className="social-icon"><TwitterIcon /></div>
              <span className="social-name">Twitter</span>
              <div className="illustration-space"></div>
            </SocialCard>
            <SocialCard href="#" target="_blank" aria-label="Telegram">
              <div className="social-icon"><TelegramIcon /></div>
              <span className="social-name">Telegram</span>
              <div className="illustration-space"></div>
            </SocialCard>
            <SocialCard href="#" target="_blank" aria-label="Discord">
              <div className="social-icon"><DiscordIcon /></div>
              <span className="social-name">Discord</span>
              <div className="illustration-space"></div>
            </SocialCard>
            <SocialCard href="#" target="_blank" aria-label="LinkedIn">
              <div className="social-icon"><LinkedInIcon /></div>
              <span className="social-name">LinkedIn</span>
              <div className="illustration-space"></div>
            </SocialCard>
          </SocialLinks>
        </FollowUsCard>
      </ContactPageContainer>
      
      <OfficesSection>
        <AnimatedSection direction="up" delay={0.3}>
          <h2 className="section-title">{t('contact.globalOffices')}</h2>
          <p className="section-subtitle">{t('contact.globalOfficesSubtitle')}</p>
          
          <OfficesList>
            {offices.map((office, index) => (
              <OfficeCard key={index}>
                <div className="office-city">{office.city}</div>
                <div className="office-address">{office.address}</div>
                <div className="office-phone">{office.phone}</div>
                <div className="office-email">
                  <a href={`mailto:${office.email}`}>{office.email}</a>
                </div>
              </OfficeCard>
            ))}
          </OfficesList>
        </AnimatedSection>
      </OfficesSection>
    </>
  );
};

export default ContactPage; 